import React from 'react';
import { ITableColumns, ITableDataSource } from 'models/table';
import {
  ITransaction,
  ITransactionDetailsAncillary,
  ITransactionDetailsFirearm,
  ITransactionDetailsMarkings,
  ITransactionItemType,
  ITransactionMarking,
} from 'models/transaction';
import { formatDate } from 'utils/date';
import { ERouteLinks } from 'models/route';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { Chips } from 'components/atoms/Chips';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { getColumnTitleFromCustomField } from 'utils/table';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { Link as LinkRouter } from 'react-router-dom';
import Link from '@mui/material/Link';
import { getAncillaryViewRoute } from 'pages/TransactionsPage/ViewTransactionPage';
import { EMarkingTypes } from 'constants/Markings';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { EAdditionalFields } from './TransactionDetailsMarkingsSection/helpers';
import { findInDictionary } from 'helpers/dictionary';
import { IDictionary } from 'models/dictionary';

export const mapTransactionsToDataSource = (
  transactions: ITransaction[],
  manufacturer: IDictionary[] | null,
) =>
  transactions.map((transaction) => ({
    id: `${transaction.transactionUniqueId}${transaction.serialNumber}`,
    transactionId: transaction.transactionUniqueId,
    date: transaction.date,
    type: transaction.type,
    state: transaction.state,
    legalityUniqueId: transaction.legalityUniqueId,
    activityUniqueId: transaction.activityUniqueId,
    typeUniqueId: transaction.typeUniqueId,
    transactionItemType: transaction.transactionItemType,
    serialNumber: transaction.serialNumber,
    owner: transaction.owner,
    keeper: transaction.keeper,
    manufacturer: findInDictionary(transaction.manufacturerUniqueId, manufacturer),
  }));

interface ITransactionsTableColumns {
  renderStateFieldColor: (arg: ITableDataSource) => any;
  midDateFormat: string;
  t: (key: string) => string;
}

export const transactionsTableColumns = ({
  midDateFormat,
  renderStateFieldColor,
  t,
}: ITransactionsTableColumns): ITableColumns[] => [
  {
    id: 'f0504c01-6b32-482d-b369-304a6f32f510',
    key: 'date',
    title: 'transactionsList.transactionDate.column',
    dateFormat: midDateFormat,
  },
  {
    id: 'f0504c02-6b32-482d-b369-304a6f32f510',
    key: 'type',
    title: 'transactionsList.transactionType.column',
    align: 'center',
    minWidth: '170px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Chips text={props.type} variant={renderStateFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504c04-6b32-482d-b369-304a6f32f510',
    key: 'owner',
    title: 'transactionsList.owner.column',
    minWidth: '150px',
  },
  {
    id: 'f0504c05-6b32-482d-b369-304a6f32f510',
    key: 'keeper',
    title: 'transactionsList.keeper.column',
    minWidth: '150px',
  },
  {
    id: '3401d192-48ba-11ee-be56-0242ac120002',
    key: 'state',
    title: 'transactionsList.state.column',
    align: 'center',
    minWidth: '130px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Chips text={props.state} variant={renderStateFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504c03-6b32-482d-b369-304a6f32f510',
    key: 'serialNumber',
    title: 'transactionsList.serialNumber.column',
  },
  {
    id: '9a7554b6-4893-11ee-be56-0242ac120002',
    key: 'transactionItemType',
    title: 'transactionsList.asset.label',
    render: (_data, props) =>
      ITransactionItemType[props.transactionItemType as keyof typeof ITransactionItemType] ===
      ITransactionItemType.FIREARM
        ? t('transaction.search.firearmType.label')
        : t('transaction.search.ancillaryType.label'),
  },
  {
    id: 'f0504c06-6b32-482d-b369-304a6f32f510',
    key: 'manufacturer',
    title: 'transactionsList.transactionType.manufacturer',
    minWidth: '220px',
  },
  {
    id: 'f0504c08-6b32-482d-b369-304a6f32f510',
    key: '',
    title: 'general.actions.button',
    isSortable: false,
    width: '80px',
    align: 'center',
    minWidth: '80px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <LinkRouter to={ERouteLinks.ViewTransaction.replace(':id', props.transactionId)}>
        <Button
          variant={EButtonVariants.outlined}
          icon={EIconTypes.view}
          sx={{ marginRight: '2px' }}
          tableButton
        />
      </LinkRouter>
    ),
  },
];

export const mapValuesToTransactionPresetForView = (
  values: { key: string; value: string }[],
  midDateFormat: string,
) =>
  values.map((item) => {
    switch (item.key) {
      case 'transactionTimeFrom':
      case 'transactionTimeTo':
      case 'createTimeFrom':
      case 'createTimeTo':
      case 'expirationTimeFrom':
      case 'expirationTimeTo':
        return {
          key: item.key,
          value: formatDate(item.value, midDateFormat),
        };
      default:
        return item;
    }
  });

interface ITransactionDetailsTableColumns {
  isDifferentMakesOrModels: boolean;
  handleView: (arg: string) => void | Promise<void> | null;
  customFields?: ICustomFieldByObjectType[];
  initial: boolean;
  t?: (key: string) => string;
  transactionType?: ETransactionTypes;
}

export const transactionDetailsFirearmsTableColumns = (
  {
    isDifferentMakesOrModels,
    handleView,
    customFields = [],
    initial,
    t,
    transactionType,
  }: ITransactionDetailsTableColumns,
  selectedLanguage?: string | undefined,
  dateFormat?: string | undefined,
): ITableColumns[] => {
  const customFieldColumns: ITableColumns[] = customFields
    .filter((customField) => customField.visibleInSearchResults)
    .map((customField) => ({
      id: customField.uniqueId,
      key: `customField.${customField.name}`,
      title: getColumnTitleFromCustomField(customField, selectedLanguage),
      isSortable: false,
      render: (data, props) => {
        if (props?.customFields?.length) {
          const cf = props.customFields.find(
            (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
          );
          if (cf) {
            if (cf.customFieldType === ECustomFieldType.BOOL && t) {
              if (cf.value === null) {
                return '-';
              }
              return cf.value
                ? t('customFields.value.yes.label')
                : t('customFields.value.no.label');
            }
            if (cf.customFieldType === ECustomFieldType.DATE && dateFormat) {
              return formatDate(cf.value, dateFormat);
            }
            return cf.value || '-';
          }
        }
        return '-';
      },
    }));

  return [
    {
      id: 'f0504ca-6b32-482d-b369-304a6f32f510',
      key: 'fireCycleId',
      title: 'transactionDetails.firearmDetails.accordion.firecycleID.label',
    },
    ...(isDifferentMakesOrModels
      ? [
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f511',
            key: 'make',
            title: 'transactionDetails.firearmDetails.accordion.make.column',
          },
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f512',
            key: 'model',
            title: 'transactionDetails.firearmDetails.accordion.model.column',
          },
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f513',
            key: 'action',
            title: 'transactionDetails.firearmDetails.accordion.firearmAction.column',
          },
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f515',
            key: 'type',
            title: 'transactionDetails.firearmDetails.accordion.type.column',
          },
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f514',
            key: 'caliber',
            title: 'transactionDetails.firearmDetails.accordion.calibre.column',
          },
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f524',
            key: 'frtId',
            title: 'transactionDetails.firearmDetails.accordion.firearmsReferenceNo.label',
            render: (_data: any) => (
              <Link
                href={ERouteLinks.FRTDetails.replace(':frtId', _data)}
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline' }}
              >
                {_data}
              </Link>
            ),
          },
        ]
      : []),
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f534',
      key: 'serialNumber',
      title: 'transactionDetails.firearmDetails.accordion.serialNumber.label',
    },
    ...(initial
      ? [
          {
            id: 'f0504ca6-6b32-482d-b369-304a6f32f549',
            key: 'marking',
            title:
              transactionType && transactionType === ETransactionTypes.DECLARED_IMPORTED
                ? 'transactionDetails.firearmDetails.accordion.importMarking.column'
                : 'transactionDetails.firearmDetails.accordion.appliedMarking.column',
          },
        ]
      : []),
    {
      id: 'f0504ca6-6b32-482d-b369-304a6f32f554',
      key: 'manufacturedAt',
      title: 'transactionDetails.firearmDetails.accordion.manufacturedDate.column',
    },
    ...customFieldColumns,
    {
      id: 'fe7a7688-1583-4233-8319-dd5c8973cc44',
      key: '',
      title: 'registeredFirearms.accordion.actions.column',
      isSortable: false,
      width: '60px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) =>
        !!handleView && (
          <Link href={`${ERouteLinks.ViewRegisteredFirearm.replace(':id', props.id)}`}>
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              sx={{ marginRight: '2px' }}
              onClick={() => handleView(props.id as string)}
              tableButton
            />
          </Link>
        ),
    },
  ];
};

interface ITransactionDetailsAncillariesTableColumns {
  isDiscard?: boolean;
  showDetail: boolean | null;
  initial: boolean;
  transactionType?: ETransactionTypes;
}

export const transactionDetailsAncillariesTableColumns = ({
  isDiscard = false,
  showDetail,
  initial,
  transactionType,
}: ITransactionDetailsAncillariesTableColumns): ITableColumns[] => [
  {
    id: 'gg504ca-6b32-482d-b369-304a6f32f510',
    key: 'fireCycleId',
    title: 'transactionDetails.ancillaryDetails.accordion.firecycleID.label',
  },
  {
    id: '6h504ca6-6b32-482d-b369-304a6f32f534',
    key: 'serialNumber',
    title: 'transactionDetails.ancillaryDetails.accordion.serialNumber.label',
  },
  {
    id: 'f0504ca6-6b32-4822-b369-304a6f32f512',
    key: 'lotNumber',
    title: 'transactionDetails.ancillaryDetails.accordion.lotNumber.label',
  },
  {
    id: 'f0504ca6-6b32-4823-b369-304a6f32f513',
    key: 'batchNumber',
    title: 'transactionDetails.ancillaryDetails.accordion.batchNumber.label',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f514',
    key: 'ancillaryDictionaryValue',
    title: 'transactionDetails.ancillaryDetails.accordion.ancillary.label',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f515',
    key: 'ancillarySpecificType',
    title: 'transactionDetails.ancillaryDetails.accordion.ancillaryType.label',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f516',
    key: 'manufacturer',
    title: 'transactionDetails.ancillaryDetails.accordion.manufacturer.label',
  },
  {
    id: 'f0504ca6-6b32-4825-b369-304a6f32f517',
    key: 'calibre',
    title: 'transactionDetails.ancillaryDetails.accordion.calibre.label',
  },
  ...(initial
    ? [
        {
          id: 'f0504ca6-6b32-482d-b369-304a6f32f549',
          key: 'marking',
          title:
            transactionType &&
            (transactionType === ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED ||
              transactionType === ETransactionTypes.DECLARED_IMPORTED)
              ? 'transactionDetails.ancillaryDetails.accordion.importMarking.column'
              : 'transactionDetails.ancillaryDetails.accordion.appliedMarking.column',
        },
      ]
    : []),
  {
    id: '347a7688-1583-4233-8319-dd5c8973cc44',
    key: '',
    title: 'transactionDetails.ancillaryDetails.accordion.actions.column',
    isSortable: false,
    width: '60px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) =>
      showDetail && (
        <LinkRouter
          to={getAncillaryViewRoute(props.ancillaryType, props.id)}
          {...(isDiscard ? { target: '_blank' } : {})}
        >
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.view}
            sx={{ marginRight: '2px' }}
            tableButton
          />
        </LinkRouter>
      ),
  },
];

export const mapTransactionDetailsFirearms = (
  items: ITransactionDetailsFirearm[],
  shortDateFormat: string,
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  firearmAction: IDictionary[] | null,
  firearmType: IDictionary[] | null,
  caliber: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    fireCycleId: item.fireCycleId,
    make: findInDictionary(item.makeUniqueId, make),
    model: findInDictionary(item.modelUniqueId, model),
    action: findInDictionary(item.actionUniqueId, firearmAction),
    type: findInDictionary(item.typeUniqueId, firearmType),
    caliber: findInDictionary(item.caliberUniqueId, caliber),
    frtId: item.frtId,
    serialNumber: item.serialNumber,
    appliedOrImportMarking: item.appliedOrImportMarking,
    marking: item.marking,
    yearOfConfiscation: item.yearOfConfiscation,
    yearOfTransfer: item.yearOfTransfer,
    customFields: item.customFields || [],
    manufacturedAt: item?.manufacturedAt
      ? formatDate(item.manufacturedAt, shortDateFormat)
      : '',
  }));

export const mapTransactionDetailsAncillaries = (
  items: ITransactionDetailsAncillary[],
  shortDateFormat: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    fireCycleId: item.fireCycleId,
    artUniqueId: item.artUniqueId,
    artId: item.artId,
    ancillaryDictionaryValue: item.ancillaryDictionaryValue,
    ancillaryType: item.ancillaryType,
    serialNumber: item.serialNumber,
    appliedOrImportMarking: item.appliedOrImportMarking,
    yearOfConfiscation: item.yearOfConfiscation,
    yearOfTransfer: item.yearOfTransfer,
    manufacturedAt: item?.productionDate
      ? formatDate(item.productionDate, shortDateFormat)
      : '-',
    manufacturer: item.manufacturer,
    ancillarySpecificType: item.ancillarySpecificType,
    calibre: item.calibre,
    lotNumber: item.lotNumber,
    batchNumber: item.batchNumber,
    marking: item.marking,
  }));
